/**
 * Resolution Drawer — pub/sub bus
 * ───────────────────────────────
 * Tiny opener registry so any execution surface can launch the unified
 * Unified Resolution Drawer without prop-drilling. Read-only contract
 * (the drawer never mutates anything). The drawer component mounts and
 * registers an opener; surfaces call `openResolutionDrawer(...)`.
 *
 * Hard rules:
 *  - No React imports here (pure module).
 *  - No writers, no side-effects beyond a single callback ref.
 */

export type ResolutionDrawerSource =
  | "live_stream"
  | "signal_cluster"
  | "install_today_blocked"
  | "qa_callback_row"
  | "holdback_row";

export type ResolutionDrawerSeverity = "info" | "warn" | "critical";

export interface ResolutionDrawerEventContext {
  kind: string;
  /** ISO timestamp of the originating event. */
  at?: string;
  headline?: string;
  detail?: string;
  /** Optional explicit severity from the launching surface. */
  severity?: ResolutionDrawerSeverity;
}

export interface OpenResolutionDrawerOptions {
  jobId: string;
  source: ResolutionDrawerSource;
  eventContext?: ResolutionDrawerEventContext;
}

type Opener = (opts: OpenResolutionDrawerOptions) => void;

let registered: Opener | null = null;

export function registerResolutionDrawerOpener(fn: Opener): () => void {
  registered = fn;
  return () => {
    if (registered === fn) registered = null;
  };
}

export function openResolutionDrawer(opts: OpenResolutionDrawerOptions): boolean {
  if (!registered) return false;
  registered(opts);
  return true;
}

export function isResolutionDrawerAvailable(): boolean {
  return registered !== null;
}

/** Test-only helper. */
export function __resetResolutionDrawerBus(): void {
  registered = null;
}
